import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiClientError, tasksApi } from '@/lib/api';
import {
  applyCopilotApproval,
  buildApprovalFeedbackPrompt,
  formatApprovalValue,
  getApprovalFieldLabel,
  getApprovalPatch,
  getApprovalTitle,
} from '@/lib/copilot-approvals';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAliceApprovalStore, type AlicePendingApprovalItem } from '@/stores/alice-approvals';
import { useUiStore } from '@/stores/ui';
import { computeQueueCounts, formatShortDate, type Task } from '@oneup/shared';
import {
  AlertCircle,
  Archive,
  ArrowRight,
  Check,
  CheckCircle2,
  Eye,
  Flame,
  Loader2,
  Send,
  Sparkles,
  Star,
  Sunrise,
  Sunset,
} from 'lucide-react';

export function CommandCenterView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['tasks', 'all'],
    queryFn: () => tasksApi.list({ limit: 300 }),
  });
  const tasks = data?.items ?? [];
  const counts = computeQueueCounts(tasks);
  const openTasks = tasks.filter(isOpenTask);
  const onFire = taggedTasks(openTasks, 'On Fire');
  const shortlist = taggedTasks(openTasks, 'Shortlist');
  const sendToClient = taggedTasks(openTasks, 'Send To Client');

  const taskMutation = useMutation({
    mutationFn: ({ task, patch }: { task: Task; patch: Partial<Task> }) =>
      tasksApi.patch(task.id, patch),
    onMutate: async ({ task, patch }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', 'all'] });
      const previous = queryClient.getQueryData<{ items: Task[] }>(['tasks', 'all']);
      if (previous) {
        queryClient.setQueryData(['tasks', 'all'], {
          ...previous,
          items: previous.items.map((item) => (item.id === task.id ? { ...item, ...patch } : item)),
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['tasks', 'all'], context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['tasks', 'all'] }),
  });

  const clearTag = (task: Task, tag: string) => {
    taskMutation.mutate({
      task,
      patch: { commandCenterFlags: task.commandCenterFlags.filter((item) => item !== tag) },
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex-row items-center gap-2">
            <Sunrise className="h-4 w-4 text-warning" aria-hidden />
            <CardTitle>Morning Open</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-[13px]">
            <Row label="Overdue" value={counts.overdue} tone={counts.overdue > 0 ? 'destructive' : undefined} />
            <Row label="Needs triage" value={counts.needs_triage} />
            <Row label="Needs client update" value={counts.needs_client_update} />
            <Row label="Ready to bill" value={counts.ready_to_bill} tone={counts.ready_to_bill > 0 ? 'success' : undefined} />
            <p className="pt-2 text-[11px] text-muted-foreground">
              Phase 5 wires the &ldquo;Start Daily Open&rdquo; walkthrough.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center gap-2">
            <Sunset className="h-4 w-4 text-info" aria-hidden />
            <CardTitle>End-of-Day Close</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-[13px]">
            <Row label="Done this week" value={counts.done_this_week} />
            <Row label="Still in progress" value={counts.in_progress} />
            <Row label="Waiting on client" value={counts.waiting_on_client} />
            <Row label="Ready to bill" value={counts.ready_to_bill} />
            <p className="pt-2 text-[11px] text-muted-foreground">
              Phase 5 wires the &ldquo;Start Daily Close&rdquo; walkthrough.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <TaggedTaskCard
          title="On Fire"
          icon={Flame}
          tasks={onFire}
          badge="On Fire"
          onOpen={(id) => navigate(`/tasks/${id}`)}
          onClose={(task) => taskMutation.mutate({ task, patch: { status: 'Closed' } })}
          onArchive={(task) => taskMutation.mutate({ task, patch: { status: 'Archived' } })}
          onClearTag={clearTag}
        />
        <TaggedTaskCard
          title="Shortlist"
          icon={Star}
          tasks={shortlist}
          badge="Shortlist"
          onOpen={(id) => navigate(`/tasks/${id}`)}
          onClose={(task) => taskMutation.mutate({ task, patch: { status: 'Closed' } })}
          onArchive={(task) => taskMutation.mutate({ task, patch: { status: 'Archived' } })}
          onClearTag={clearTag}
        />
        <TaggedTaskCard
          title="Send To Client"
          icon={Send}
          tasks={sendToClient}
          badge="Send To Client"
          onOpen={(id) => navigate(`/tasks/${id}`)}
          onClose={(task) => taskMutation.mutate({ task, patch: { status: 'Closed' } })}
          onArchive={(task) => taskMutation.mutate({ task, patch: { status: 'Archived' } })}
          onClearTag={clearTag}
        />
        <AlicePendingApprovalsCard />
      </div>
    </div>
  );
}

function AlicePendingApprovalsCard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const items = useAliceApprovalStore((state) => state.items);
  const removeApproval = useAliceApprovalStore((state) => state.removeApproval);
  const setApprovalError = useAliceApprovalStore((state) => state.setApprovalError);
  const setApprovalFeedback = useAliceApprovalStore((state) => state.setApprovalFeedback);
  const setNotice = useUiStore((state) => state.setNotice);
  const openCopilotPanel = useUiStore((state) => state.openCopilotPanel);
  const seedCopilotPanel = useUiStore((state) => state.seedCopilotPanel);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) ?? null,
    [items, selectedId],
  );

  const approve = async (item: AlicePendingApprovalItem) => {
    setBusyId(item.id);
    setApprovalError(item.id, null);
    try {
      const applied = await applyCopilotApproval(item.pending);
      for (const queryKey of applied.invalidate) {
        void queryClient.invalidateQueries({ queryKey });
      }
      removeApproval(item.id);
      setNotice(applied.notice);
      if (applied.navigateTo) navigate(applied.navigateTo);
      if (selectedId === item.id) setSelectedId(null);
    } catch (err) {
      const message = formatApprovalError(err);
      setApprovalError(item.id, message);
      setNotice(message, 'destructive');
    } finally {
      setBusyId(null);
    }
  };

  const openDetails = (item: AlicePendingApprovalItem) => {
    setSelectedId(item.id);
    setFeedback(item.feedback ?? '');
  };

  const regenerate = () => {
    if (!selected) return;
    const trimmed = feedback.trim();
    if (!trimmed) {
      setApprovalError(selected.id, 'Add feedback before asking Alice to regenerate.');
      return;
    }
    setApprovalFeedback(selected.id, trimmed);
    openCopilotPanel();
    seedCopilotPanel(buildApprovalFeedbackPrompt(selected.pending, trimmed));
    setNotice('Sent feedback to Alice for regeneration.');
    setSelectedId(null);
  };

  return (
    <Card className="xl:col-span-3">
      <CardHeader className="flex-row items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" aria-hidden />
        <CardTitle>Alice Pending Approvals</CardTitle>
        <Badge variant="outline" className="ml-auto">
          {items.length}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 ? (
          <p className="py-5 text-center text-[12px] text-muted-foreground">
            No Alice actions are waiting for approval.
          </p>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {items.map((item) => (
              <div
                key={item.id}
                className="rounded-md border border-border bg-background/70 px-3 py-2"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-medium text-foreground">
                      {getApprovalTitle(item.pending)}
                    </p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {item.pending.toolName} - {formatRelativeTime(item.createdAt)}
                    </p>
                  </div>
                  <Badge variant="warning" className="shrink-0">
                    Pending
                  </Badge>
                </div>
                {item.error && (
                  <div className="mt-2 flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1.5 text-[11px] text-destructive">
                    <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                    <span>{item.error}</span>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap justify-end gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1 px-2 text-[11px]"
                    onClick={() => openDetails(item)}
                    disabled={busyId === item.id}
                  >
                    <Eye className="h-3 w-3" aria-hidden />
                    Open
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 gap-1 px-2 text-[11px]"
                    onClick={() => void approve(item)}
                    disabled={busyId === item.id}
                  >
                    {busyId === item.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    ) : (
                      <Check className="h-3 w-3" aria-hidden />
                    )}
                    Approve
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <ApprovalDetailDialog
        item={selected}
        feedback={feedback}
        onFeedbackChange={setFeedback}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
        onApprove={(item) => void approve(item)}
        onRegenerate={regenerate}
        busy={selected ? busyId === selected.id : false}
      />
    </Card>
  );
}

function ApprovalDetailDialog({
  item,
  feedback,
  onFeedbackChange,
  onOpenChange,
  onApprove,
  onRegenerate,
  busy,
}: {
  item: AlicePendingApprovalItem | null;
  feedback: string;
  onFeedbackChange: (value: string) => void;
  onOpenChange: (open: boolean) => void;
  onApprove: (item: AlicePendingApprovalItem) => void;
  onRegenerate: () => void;
  busy: boolean;
}) {
  const patch = item ? getApprovalPatch(item.pending) : {};
  const entries = Object.entries(patch);

  return (
    <Dialog open={Boolean(item)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        {item && (
          <>
            <DialogHeader>
              <DialogTitle>{getApprovalTitle(item.pending)}</DialogTitle>
              <DialogDescription>
                Review what Alice proposed, approve it, or send feedback back to Alice for a regenerated action.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {item.pending.summary && (
                <div className="rounded-md border border-border bg-card px-3 py-2 text-[12px] whitespace-pre-wrap text-foreground">
                  {item.pending.summary}
                </div>
              )}

              {entries.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Proposed details
                  </div>
                  <div className="max-h-72 space-y-1.5 overflow-auto pr-1">
                    {entries.map(([key, value]) => (
                      <div
                        key={key}
                        className="rounded-md border border-border bg-background/70 px-3 py-2 text-[12px]"
                      >
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          {getApprovalFieldLabel(key)}
                        </div>
                        <pre className="mt-1 whitespace-pre-wrap break-words font-sans text-foreground">
                          {formatApprovalValue(value)}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {item.error && (
                <div className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>{item.error}</span>
                </div>
              )}

              <label className="block space-y-1.5">
                <span className="text-[12px] font-medium text-foreground">Feedback for Alice</span>
                <textarea
                  value={feedback}
                  onChange={(event) => onFeedbackChange(event.target.value)}
                  placeholder="Tell Alice what to change before she regenerates this action."
                  className="min-h-24 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-[13px] leading-relaxed ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </label>
            </div>

            <DialogFooter className="gap-2 sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={onRegenerate}
                disabled={busy}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" aria-hidden />
                Regenerate with feedback
              </Button>
              <Button
                type="button"
                onClick={() => onApprove(item)}
                disabled={busy}
                className="gap-1.5"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Check className="h-4 w-4" aria-hidden />
                )}
                Approve
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TaggedTaskCard({
  title,
  icon: Icon,
  tasks,
  badge,
  onOpen,
  onClose,
  onArchive,
  onClearTag,
}: {
  title: string;
  icon: typeof Flame;
  tasks: Task[];
  badge: string;
  onOpen: (id: string) => void;
  onClose: (task: Task) => void;
  onArchive: (task: Task) => void;
  onClearTag: (task: Task, tag: string) => void;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2">
        <Icon className="h-4 w-4 text-primary" aria-hidden />
        <CardTitle>{title}</CardTitle>
        <Badge variant="outline" className="ml-auto">
          {tasks.length}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-2">
        {tasks.length === 0 ? (
          <p className="py-4 text-center text-[12px] text-muted-foreground">No open tasks tagged {badge}.</p>
        ) : (
          tasks.slice(0, 6).map((task) => (
            <div key={task.id} className="rounded-md border border-border transition-colors hover:bg-muted/20">
            <button
              type="button"
              onClick={() => onOpen(task.id)}
              className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left"
            >
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-medium text-foreground">{task.name}</span>
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  {task.client?.name ?? 'No client'}
                  {task.targetDate ? ` · due ${formatShortDate(task.targetDate)}` : ''}
                </span>
              </span>
              <ArrowRight className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
            </button>
            <div className="mt-2 flex flex-wrap gap-1.5 px-3 pb-2">
              <Button type="button" size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]" onClick={() => onClose(task)}>
                <CheckCircle2 className="h-3 w-3" aria-hidden />
                Close
              </Button>
              <Button type="button" size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]" onClick={() => onArchive(task)}>
                <Archive className="h-3 w-3" aria-hidden />
                Archive
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => onClearTag(task, badge)}>
                Clear tag
              </Button>
            </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function taggedTasks(tasks: Task[], tag: string): Task[] {
  return tasks.filter((task) => task.commandCenterFlags.includes(tag));
}

function isOpenTask(task: Task): boolean {
  const status = task.status.trim().toLowerCase();
  return task.semanticStatus !== 'done' && status !== 'closed' && status !== 'archived' && status !== 'done';
}

function formatApprovalError(err: unknown): string {
  if (err instanceof ApiClientError) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return 'Approval failed.';
}

function formatRelativeTime(value: string): string {
  const then = Date.parse(value);
  if (Number.isNaN(then)) return 'just now';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'destructive' | 'success';
}) {
  const valueClass =
    tone === 'destructive' && value > 0
      ? 'text-destructive'
      : tone === 'success' && value > 0
        ? 'text-success'
        : '';
  return (
    <div className="flex items-center justify-between border-b border-border/60 pb-1.5 last:border-0 last:pb-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-semibold tabular-nums ${valueClass}`}>{value}</span>
    </div>
  );
}
